import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../api/client';
import { X, Loader2, Check } from 'lucide-react';
import { useCan } from '../../../lib/rbac';

function PublishSettingsPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const can = useCan();
  const [autoPublish, setAutoPublish] = useState(false);
  const [minScore, setMinScore] = useState(85);
  const [layerB, setLayerB] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get<any>('/publisher/settings').then(res => {
      if (res.success && res.data) {
        const ap = res.data.auto_publish || {};
        setAutoPublish(!!ap.enabled);
        setMinScore(ap.min_score ?? 85);
        setLayerB(!!(res.data.layer_b?.enabled ?? true));
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/publisher/settings', {
        auto_publish: { enabled: autoPublish, min_score: minScore },
        layer_b: { enabled: layerB },
      });
      onClose();
    } catch (err) {
      console.error('Failed to save settings:', err);
    } finally {
      setSaving(false);
    }
  };

  // Ajustes globales del publicador: sin la llave el panel no se abre.
  if (!open || !can('publisher.settings')) return null;

  return (
    <div className="absolute right-0 top-full mt-2 z-50 w-80 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg shadow-lg p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--color-text)]">{t('pub.settingsTitle', 'Pipeline Settings')}</h3>
        <button onClick={onClose} className="text-[var(--color-text-secondary)] hover:text-[var(--color-text)]">
          <X size={16} />
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-4"><Loader2 size={20} className="animate-spin text-[var(--color-text-secondary)]" /></div>
      ) : (
        <>
          <div className="space-y-3">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <div className="text-sm font-medium text-[var(--color-text)]">{t('pub.autoPublish', 'Auto-publish')}</div>
                <div className="text-xs text-[var(--color-text-secondary)]">{t('pub.autoPublishDesc', 'Publish drafts automatically when score meets threshold')}</div>
              </div>
              <input
                type="checkbox"
                checked={autoPublish}
                onChange={e => setAutoPublish(e.target.checked)}
                className="w-4 h-4 rounded border-[var(--color-border)] text-[var(--color-primary)]"
              />
            </label>

            <div className={autoPublish ? '' : 'opacity-40 pointer-events-none'}>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1">
                {t('pub.minScore', 'Min score')}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min={60}
                  max={100}
                  step={5}
                  value={minScore}
                  onChange={e => setMinScore(Number(e.target.value))}
                  className="flex-1"
                />
                <span className="text-sm font-mono text-[var(--color-text)] w-8 text-right">{minScore}</span>
              </div>
            </div>

            <hr className="border-[var(--color-border)]" />

            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <div className="text-sm font-medium text-[var(--color-text)]">{t('pub.layerB', 'Layer B Enhancement')}</div>
                <div className="text-xs text-[var(--color-text-secondary)]">{t('pub.layerBDesc', 'AI enhancement pass before publishing')}</div>
              </div>
              <input
                type="checkbox"
                checked={layerB}
                onChange={e => setLayerB(e.target.checked)}
                className="w-4 h-4 rounded border-[var(--color-border)] text-[var(--color-primary)]"
              />
            </label>
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[var(--color-primary)] text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-colors"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            {t('pub.saveSettings', 'Save')}
          </button>
        </>
      )}
    </div>
  );
}

export { PublishSettingsPanel };
